import { IntlProvider } from 'react-intl';
import {
  type LayoutRouteProps,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { LoadingSpinner } from '@shieldpay/bumblebee/loading-spinner';

import { hiveTheme } from '@shieldpay/theme-provider-ui/theme/hive';
import { isHydratedRef } from '@shieldpay/utilities-ui/is-hydrated-ref';
import { useServerOnlyNonce } from '@shieldpay/wheeljack/react-router/nonce-context.ts';

import hiveMessages from '@shieldpay/i18n/hive/en-GB.json';
import defaultMessages from '@shieldpay/i18n/shared/en-GB.json';

export function Layout({ children }: LayoutRouteProps) {
  const nonce = useServerOnlyNonce();

  return (
    <html lang="en-GB" ref={isHydratedRef}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <Meta />
        <Links />
      </head>
      <IntlProvider
        locale="en-GB"
        messages={{ ...defaultMessages, ...hiveMessages }}
      >
        <body className={hiveTheme}>
          {children}

          <ScrollRestoration nonce={nonce} />
          <Scripts nonce={nonce} />
        </body>
      </IntlProvider>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: { error: unknown }) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main>
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre>
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}

export function HydrateFallback() {
  return <LoadingSpinner center size="large" />;
}
